import { AxiosResponse } from 'axios';
import { apiClient, getAuthHeaders } from './index';

// ========== Request / Response types ==========

export interface CreateNarrativeRequest {
  name: string;
  content?: string;
}

export interface UpdateNarrativeRequest {
  name?: string;
  content?: string;
}

export interface NarrativeStakeholderDto {
  twitterHandle: string;
  name?: string;
  description?: string;
}

export interface SetStakeholdersRequest {
  stakeholders: NarrativeStakeholderDto[];
}

export interface SetTokensRequest {
  tokenAddresses: string[];
}

export interface NarrativeEventDto {
  description: string;
  timestamp: string;
}

export interface SetEventsRequest {
  events: NarrativeEventDto[];
}

export interface McapTimelinePoint {
  timestamp: string;
  marketCap: number;
}

export interface NarrativeSummaryToken {
  address: string;
  symbol: string;
  name: string;
  marketCap: number;
  mcapTimeline: McapTimelinePoint[];
}

export interface NarrativeSummaryDto {
  id: string;
  name: string;
  keywords: string[];
  totalMarketCap: number;
  change24h: number | null;
  timeline: string | null;
  tokenCount: number;
  top3Tokens: NarrativeSummaryToken[];
  athMcap: number | null;
  dropFromATH: number | null;
}

export interface GetAllNarrativesResponse {
  data: NarrativeSummaryDto[];
  meta: { count: number };
}

export interface NarrativeDetailDto {
  id: string;
  name: string;
  content?: string;
  stakeholders: NarrativeStakeholderDto[];
  tokenAddresses: string[];
  keywords: string[];
  events: NarrativeEventDto[];
  createdAt: string;
  updatedAt: string;
}

export interface GetNarrativeByIdResponse {
  data: NarrativeDetailDto;
  meta: { id: string };
}

export interface SearchNarrativesResponse {
  data: NarrativeDetailDto[];
  meta: { keyword: string; count: number };
}

export interface TimelineTokenMarketCap {
  address: string;
  symbol: string;
  marketCap: number;
}

export interface TimelineDataPoint {
  timestamp: string;
  totalMarketCap: number;
  tokenCount: number;
  tokenMarketCaps: TimelineTokenMarketCap[];
}

export interface NarrativeStatisticsTimeline {
  startDate: string;
  endDate: string;
  interval: string;
  dataPoints: TimelineDataPoint[];
}

export interface NarrativeStatisticsDto {
  id: string;
  name: string;
  timeline: NarrativeStatisticsTimeline;
}

export interface GetNarrativeStatisticsResponse {
  data: NarrativeStatisticsDto;
  meta: { id: string; days: number; topN: number };
}

// ========== API ==========

const BASE = '/fe-management-system/narratives';

export const narrativeApi = {
  /**
   * Create a new narrative with name and optional content.
   * POST /fe-management-system/narratives
   */
  async createNarrative(
    request: CreateNarrativeRequest
  ): Promise<{ data: NarrativeDetailDto; meta?: Record<string, unknown> }> {
    const response: AxiosResponse<{ data: NarrativeDetailDto; meta?: Record<string, unknown> }> =
      await apiClient.post(BASE, request, {
        headers: getAuthHeaders(),
      });
    return response.data;
  },

  /**
   * Get all narrative summaries sorted by total market cap (descending).
   * GET /fe-management-system/narratives
   */
  async getAllNarratives(): Promise<GetAllNarrativesResponse> {
    const response: AxiosResponse<GetAllNarrativesResponse> = await apiClient.get(BASE, {
      headers: getAuthHeaders(),
    });
    return response.data;
  },

  /**
   * Search narratives by name keyword (partial match).
   * GET /fe-management-system/narratives/search?keyword=...&limit=...
   */
  async searchNarratives(
    keyword: string,
    limit: number = 20
  ): Promise<SearchNarrativesResponse> {
    const response: AxiosResponse<SearchNarrativesResponse> = await apiClient.get(
      `${BASE}/search`,
      {
        params: { keyword, limit },
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  },

  /**
   * Get detailed statistics for a narrative (timeline, ATH, token market caps).
   * GET /fe-management-system/narratives/:id/statistics?days=...&topN=...
   */
  async getNarrativeStatistics(
    id: string,
    options: { days?: number; topN?: number } = {}
  ): Promise<GetNarrativeStatisticsResponse> {
    const { days = 7, topN = 10 } = options;
    const response: AxiosResponse<GetNarrativeStatisticsResponse> = await apiClient.get(
      `${BASE}/${id}/statistics`,
      {
        params: { days, topN },
        headers: getAuthHeaders(),
      }
    );
    return response.data;
  },

  /**
   * Get a single narrative by ID.
   * GET /fe-management-system/narratives/:id
   */
  async getNarrativeById(id: string): Promise<GetNarrativeByIdResponse> {
    const response: AxiosResponse<GetNarrativeByIdResponse> = await apiClient.get(
      `${BASE}/${id}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Update narrative name and/or content.
   * PATCH /fe-management-system/narratives/:id
   */
  async updateNarrative(
    id: string,
    request: UpdateNarrativeRequest
  ): Promise<GetNarrativeByIdResponse> {
    const response: AxiosResponse<GetNarrativeByIdResponse> = await apiClient.patch(
      `${BASE}/${id}`,
      request,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Set stakeholders for a narrative (overwrites existing).
   * PUT /fe-management-system/narratives/:id/stakeholders
   */
  async setNarrativeStakeholders(
    id: string,
    request: SetStakeholdersRequest
  ): Promise<GetNarrativeByIdResponse> {
    const response: AxiosResponse<GetNarrativeByIdResponse> = await apiClient.put(
      `${BASE}/${id}/stakeholders`,
      request,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Set token addresses for a narrative (overwrites existing). Keywords are recalculated by the backend.
   * PUT /fe-management-system/narratives/:id/tokens
   */
  async setNarrativeTokens(
    id: string,
    request: SetTokensRequest
  ): Promise<GetNarrativeByIdResponse> {
    const response: AxiosResponse<GetNarrativeByIdResponse> = await apiClient.put(
      `${BASE}/${id}/tokens`,
      request,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Set events for a narrative (overwrites existing).
   * PUT /fe-management-system/narratives/:id/events
   */
  async setNarrativeEvents(
    id: string,
    request: SetEventsRequest
  ): Promise<GetNarrativeByIdResponse> {
    const response: AxiosResponse<GetNarrativeByIdResponse> = await apiClient.put(
      `${BASE}/${id}/events`,
      request,
      { headers: getAuthHeaders() }
    );
    return response.data;
  },

  /**
   * Delete a narrative by ID.
   * DELETE /fe-management-system/narratives/:id
   */
  async deleteNarrative(id: string): Promise<void> {
    await apiClient.delete(`${BASE}/${id}`, {
      headers: getAuthHeaders(),
    });
  },
};
